/* eslint-disable simple-import-sort/imports */
import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import i18next from "i18next";
import { FTShiftDemand, PostDemandShift, ShiftDemandState } from "./shift-demand-types";
import { setSupervisorRateLimitError } from "../../../features/user/user-slice";
import { getFullTimeShiftsMeta } from "../../full-time-shifts/store/full-time-shifts-slice";
import axios from "../../../utils/api-client";


const initialState: ShiftDemandState = {
	isShiftDemandOpen: false,
	allWorkers: [],
	allWorkersObj: {},
	searchedWorkers: [],
	schedules: [],
	searchTerm: "",
	filterData: {
		allJobRoles: [],
		allLocations: [],
		allWorkcenters: [],
		allWorkcentersObj: {},
		allSkills: [],
		allManagers: [],
		jobSkillRelations: [],
		allJobsObj: {},
		shiftReasons: {},
		shiftPatternsWithDates: [],
		workerSchedulerLocations: [],
		workerTypes: [],
		workerTypesObj: {},
		workerStatuses: [],
		workerStatusesObj: {},
	},
	selectedLocation: null,
	ftShiftDemands: [],
	ftWorkerWorkcenters: [],
	ftWorkerShiftPatterns: [],
	ftWorkerPrimaryJobs: [],
	ftWorkerFlexShifts: [],
	getFTShiftDemands: {
		status: "idle",
		errorMessage: "",
	},
	postFTShiftDemands: {
		status: "idle",
		errorMessage: "",
	},
	fetchFTWorkerFilters: {
		status: "idle",
		errorMessage: "",
	},
};

export const getFTShiftDemands = createAsyncThunk(
	"shiftDemand/getShiftDemands",
	async (

		params: { startDate?: string; endDate?: string },
		{ dispatch, rejectWithValue, getState }
	) => {

		const state:any = getState()
		const selectedLocation = state.fullTimeShifts.selectedLocation;
		try {
			const response = await axios.get<FTShiftDemand[]>(
				`${process.env.REACT_APP_API_END_POINT}/FTShiftsView/GetFTShiftDemands`,
				{
					params:{
						startDateTime: params?.startDate ??  new Date().toISOString(),
						endDateTime: params?.endDate ?? new Date(new Date().setDate(new Date().getDate() + 30)).toISOString(),
						locationId: selectedLocation || null,
					}
				}
			);
			return { data: response.data };
		} catch (error: any) {
			const errorResponse = error?.response?.data;
			errorResponse.status = error?.response?.status;
			if (errorResponse.status === 429) {
				dispatch(setSupervisorRateLimitError());
				return rejectWithValue(errorResponse);
			}
			if (error.response.status === 404) {
				return rejectWithValue(error.response.data.message);
			}
			return rejectWithValue(error.response.data.message);
		}
	}
);

export const postFTShiftDemands = createAsyncThunk(
	"shiftDemand/postShiftDemands",
	async (
		params: { demands: PostDemandShift[]; currentDate?: string },
		{ dispatch, rejectWithValue, getState }
	) => {
		try {
			const response = await axios.post(
				`${process.env.REACT_APP_API_END_POINT}/FTShiftsView/SetFTShiftDemands`,
				[...params.demands],
			);
			const responseData = response.data;

            if (responseData && responseData.length > 0) {
                const shiftDetails = responseData.map((shift: any) => ({
                    shiftId: shift.shiftId,
                    workcenterId: shift.workcenterId,
                }));
                dispatch(getFullTimeShiftsMeta(shiftDetails));
            }
            return responseData;
		} catch (error: any) {
			const errorResponse = error?.response?.data;
			errorResponse.status = error?.response?.status;
			if (errorResponse.status === 429) {
				dispatch(setSupervisorRateLimitError());
				return rejectWithValue(errorResponse);
			}
			if (error.response.status === 404) {
				return rejectWithValue(error.response.data.message);
			}
			return rejectWithValue(error.response.data.message);
		}
	}
);

const ShiftDemandSlice = createSlice({
	name: "shiftDemand",
	initialState,
	reducers: {
		setShiftDemandOpen: (state) => {
			state.isShiftDemandOpen = true;
		},
		resetShiftDemandOpen: (state) => {
			state.isShiftDemandOpen = false;
		},
		resetPostShiftDemandStatus: (state) => {
			state.postFTShiftDemands.status = "idle";
			state.postFTShiftDemands.errorMessage = "";
		},
	},
	extraReducers: (builder) => {
		builder.addCase(getFTShiftDemands.pending, (state) => {
			state.getFTShiftDemands.status = "pending";
			state.getFTShiftDemands.errorMessage = "";
		});
		builder.addCase(getFTShiftDemands.fulfilled, (state, action) => {
			state.getFTShiftDemands.status = "fulfilled";
			state.getFTShiftDemands.errorMessage = "";
			state.ftShiftDemands = action.payload.data;
		});
		builder.addCase(getFTShiftDemands.rejected, (state, action) => {
			state.getFTShiftDemands.status = "error";
			state.getFTShiftDemands.errorMessage =
				action.payload as string || i18next.t("shift-demand_failed-fetch-demands");
			state.ftShiftDemands = [];
		}
		);
		builder.addCase(postFTShiftDemands.pending, (state) => {
			state.postFTShiftDemands.status = "pending";
			state.postFTShiftDemands.errorMessage = "";
		});
		builder.addCase(postFTShiftDemands.fulfilled, (state, action) => {
			state.postFTShiftDemands.status = "fulfilled";
			state.postFTShiftDemands.errorMessage = "";
			state.isShiftDemandOpen = false;
		}
		);
		builder.addCase(postFTShiftDemands.rejected, (state, action) => {
			state.postFTShiftDemands.status = "error";
			state.postFTShiftDemands.errorMessage =
				action.payload as string || i18next.t("shift-demand_failed-post-demands");
		}
		);	
	},
});

export const { setShiftDemandOpen, resetShiftDemandOpen, resetPostShiftDemandStatus } =
	ShiftDemandSlice.actions;

export default ShiftDemandSlice.reducer;
